import { type Transaction, type TransactionLineItem, type TransactionType, type AllocationHistoryEntry } from "@/types/transaction";
import { calculateTransactionStatus } from "@/lib/utils/transaction-status";

// Mock customers - matching the IDs from mock-customers.ts
const mockCustomers = [
  { id: 1, name: "Acme Corporation" },
  { id: 2, name: "Global Tech Industries" },
  { id: 3, name: "Mega Retail Group" },
  { id: 4, name: "Premier Manufacturing" },
  { id: 5, name: "Sunrise Enterprises" },
  { id: 6, name: "Quantum Solutions" },
  { id: 7, name: "Atlantic Trading Co" },
  { id: 8, name: "Pacific Imports" },
  { id: 9, name: "Northern Logistics" },
  { id: 10, name: "Summit Distribution" },
  { id: 11, name: "Coastal Ventures" },
  { id: 12, name: "Mountain Peak Corp" },
  { id: 13, name: "Valley Services" },
  { id: 14, name: "Riverside Industries" },
  { id: 15, name: "Lakeside Trading" },
  { id: 16, name: "Forest Products Inc" },
  { id: 17, name: "Desert Solutions" },
  { id: 18, name: "Prairie Enterprises" },
  { id: 19, name: "Canyon Distribution" },
  { id: 20, name: "Meadow Manufacturing" },
];

// Mock users
const mockUsers = [
  "John Smith",
  "Sarah Johnson",
  "Michael Chen",
  "Emily Davis",
  "Robert Wilson",
];

// Line item descriptions pool
const lineItemDescriptions = [
  "Professional Services - Consulting",
  "Software License - Annual Subscription",
  "Hardware Equipment - Server",
  "Maintenance & Support",
  "Training Services",
  "Implementation Services",
  "Cloud Storage - Monthly",
  "API Access - Premium Tier",
  "Custom Development",
  "Technical Support - Priority",
];

// Transaction descriptions pool
const transactionDescriptions = [
  "Monthly service invoice",
  "Annual subscription renewal",
  "Project milestone payment",
  "Quarterly maintenance fee",
  "Custom development work",
  "Support services rendered",
  "Product delivery and installation",
  "Consulting services - Phase 1",
  "License upgrade and migration",
  "Emergency support services",
];

// Generate random date within last 3 months
function randomDate(): string {
  const end = new Date();
  const start = new Date();
  start.setMonth(start.getMonth() - 3);
  const date = new Date(start.getTime() + Math.random() * (end.getTime() - start.getTime()));
  return date.toISOString().split('T')[0];
}

// Generate random future date (for due dates)
function randomFutureDate(fromDate: string): string {
  const date = new Date(fromDate);
  const daysToAdd = Math.floor(Math.random() * 60) + 15; // 15-75 days
  date.setDate(date.getDate() + daysToAdd);
  return date.toISOString().split('T')[0];
}

// Generate random past date (for overdue)
function randomPastDate(fromDate: string): string {
  const date = new Date(fromDate);
  const daysToSubtract = Math.floor(Math.random() * 30) + 1; // 1-30 days ago
  date.setDate(date.getDate() - daysToSubtract);
  return date.toISOString().split('T')[0];
}

// Generate line items for a transaction
function generateLineItems(transactionAmount: number): TransactionLineItem[] {
  const itemCount = Math.floor(Math.random() * 5) + 1; // 1-5 items
  const items: TransactionLineItem[] = [];
  
  // Calculate base amount per item
  let remainingAmount = transactionAmount;
  
  for (let i = 0; i < itemCount; i++) {
    const isLastItem = i === itemCount - 1;
    const quantity = Math.floor(Math.random() * 10) + 1;
    
    // For last item, use remaining amount, otherwise random portion
    const subtotal = isLastItem 
      ? remainingAmount 
      : Math.floor(remainingAmount * (Math.random() * 0.5 + 0.2)); // 20-70% of remaining
    
    remainingAmount -= subtotal;
    
    const hasTax = Math.random() > 0.5;
    const hasDiscount = Math.random() > 0.7;
    
    const taxAmount = hasTax ? Math.round(subtotal * 0.1) : undefined;
    
    const total = subtotal + (taxAmount || 0);
    const unitPrice = Math.round(subtotal / quantity);
    
    items.push({
      id: i + 1,
      description: lineItemDescriptions[Math.floor(Math.random() * lineItemDescriptions.length)],
      quantity,
      unitPrice,
      subtotal,
      taxAmount,
      total,
    });
  }
  
  return items;
}

// Generate allocation history for a transaction
function generateAllocationHistory(
  transaction: { id: number; referenceNumber: string; transactionDate: string; amount: number; currency: string; type: TransactionType },
  customer: { id: number; name: string }
): AllocationHistoryEntry[] {
  const entries: AllocationHistoryEntry[] = [];
  const entryCount = Math.floor(Math.random() * 3) + 1; // 1-3 allocation entries
  
  let remainingAmount = transaction.amount;
  
  for (let i = 0; i < entryCount; i++) {
    const isLastEntry = i === entryCount - 1;
    const allocatedValue = isLastEntry 
      ? remainingAmount 
      : Math.round(remainingAmount * (Math.random() * 0.5 + 0.3)); // 30-80% of remaining
    
    remainingAmount -= allocatedValue;
    
    // Generate allocation date (after transaction date)
    const transDate = new Date(transaction.transactionDate);
    const daysAfter = Math.floor(Math.random() * 30) + 1; // 1-30 days after transaction
    const allocDate = new Date(transDate);
    allocDate.setDate(allocDate.getDate() + daysAfter);
    
    entries.push({
      id: i + 1,
      accountNumber: `ACC-${customer.id.toString().padStart(5, '0')}`,
      customerName: customer.name,
      transactionDate: transaction.transactionDate,
      type: transaction.type,
      referenceNumber: transaction.referenceNumber,
      gross: transaction.amount,
      allocatedValue,
      allocatedDate: allocDate.toISOString().split('T')[0],
      currency: transaction.currency,
      currencyGross: transaction.amount,
      currencyAllocValue: allocatedValue,
      currencyCurrency: transaction.currency,
      allocationRef: `ALLOC-${String(Math.floor(Math.random() * 100000)).padStart(6, '0')}`,
      userName: mockUsers[Math.floor(Math.random() * mockUsers.length)],
    });
  }
  
  return entries;
}

// Note: Status is now calculated dynamically based on outstanding amount and due date
// using the calculateTransactionStatus utility function

function calculateInvoiceOutstanding(amount: number, transactionDate: string): { outstandingAmount: number; dueDate: string } {
  const isCompleted = Math.random() < 0.3; // 30% completed
  if (isCompleted) {
    return { outstandingAmount: 0, dueDate: randomFutureDate(transactionDate) };
  }
  const outstandingAmount = Math.round(amount * (Math.random() * 0.7 + 0.3)); // 30-100% outstanding
  const shouldBeOverdue = Math.random() < 0.3;
  const dueDate = shouldBeOverdue ? randomPastDate(transactionDate) : randomFutureDate(transactionDate);
  return { outstandingAmount, dueDate };
}

function calculateReceiptOutstanding(amount: number): { outstandingAmount: number; dueDate: undefined } {
  const outstandingAmount = Math.random() < 0.4
    ? Math.round(amount * (Math.random() * 0.8 + 0.2))
    : 0;
  return { outstandingAmount, dueDate: undefined };
}

function calculateOtherOutstanding(amount: number): { outstandingAmount: number; dueDate: undefined } {
  const outstandingAmount = Math.random() < 0.8 ? 0 : Math.round(amount * (Math.random() * 0.5 + 0.1));
  return { outstandingAmount, dueDate: undefined };
}

function getOutstandingAndDueDate(type: TransactionType, amount: number, transactionDate: string): { outstandingAmount: number; dueDate: string | undefined } {
  if (type === "invoice") return calculateInvoiceOutstanding(amount, transactionDate);
  if (type === "receipt") return calculateReceiptOutstanding(amount);
  return calculateOtherOutstanding(amount);
}

function addTypeSpecificFields(transaction: Transaction, type: TransactionType): void {
  if (type === "receipt") {
    transaction.paymentMethod = ["Credit Card", "Bank Transfer", "Check", "ACH"][Math.floor(Math.random() * 4)];
    transaction.headerReference = `INV-${String(Math.floor(Math.random() * 10000)).padStart(5, '0')}`;
  }
  if (type === "credit_note") {
    transaction.headerReference = `INV-${String(Math.floor(Math.random() * 10000)).padStart(5, '0')}`;
  }
}

function addOptionalFields(
  transaction: Transaction,
  { hasLineItems, hasDocument, type, amount, customer }: {
    hasLineItems: boolean; hasDocument: boolean; type: TransactionType;
    amount: number; customer: { id: number; name: string };
  }
): void {
  if (hasLineItems) {
    transaction.lineItems = generateLineItems(amount);
  }
  if (hasDocument) {
    transaction.documentUrl = `https://example.com/documents/${transaction.referenceNumber}.pdf`;
  }
  if ((type === "receipt" || type === "invoice") && Math.random() < 0.3) {
    transaction.allocationHistory = generateAllocationHistory(
      {
        id: transaction.id,
        referenceNumber: transaction.referenceNumber,
        transactionDate: transaction.transactionDate,
        amount: transaction.amount,
        currency: transaction.currency,
        type: transaction.transactionType,
      },
      customer
    );
    transaction.hasAllocationHistory = true;
  }
}

// Generate mock transactions
export function generateMockTransactions(): Transaction[] {
  const transactions: Transaction[] = [];
  const totalCount = 150;
  
  // Distribution: 85% invoices, 6.5% credit notes, 3% debit adj, 2% credit adj, 3.5% receipts
  const typeDistribution: TransactionType[] = [
    ...Array(127).fill("invoice"),
    ...Array(10).fill("credit_note"),
    ...Array(5).fill("debit_adjustment"),
    ...Array(3).fill("credit_adjustment"),
    ...Array(5).fill("receipt"),
  ];
  
  // Currency distribution: 100% GBP (UK-centric)
  const currencyDistribution = [
    ...Array(150).fill("GBP"),  // 100%
  ];
  
  for (let i = 0; i < totalCount; i++) {
    const type = typeDistribution[i];
    const customer = mockCustomers[Math.floor(Math.random() * mockCustomers.length)];
    const transactionDate = randomDate();
    const amount = Math.floor(Math.random() * 99000) + 1000; // £1,000 - £100,000
    const currency = currencyDistribution[Math.floor(Math.random() * currencyDistribution.length)];
    const hasNotes = Math.random() > 0.7;
    const hasModified = Math.random() > 0.6;
    const hasLineItems = (type === "invoice" || type === "credit_note") && Math.random() > 0.3;
    const hasDocument = (type === "invoice" || type === "credit_note" || type === "receipt") && Math.random() > 0.4;
    
    const createdBy = mockUsers[Math.floor(Math.random() * mockUsers.length)];
    const modifiedBy = hasModified ? mockUsers[Math.floor(Math.random() * mockUsers.length)] : undefined;
    
    // Calculate VAT and Net amounts (VAT rate of 20%)
    const vatRate = 0.20;
    const netAmount = Math.round(amount / (1 + vatRate));
    const vatAmount = amount - netAmount;
    
    const { outstandingAmount, dueDate } = getOutstandingAndDueDate(type, amount, transactionDate);
    const status = calculateTransactionStatus(outstandingAmount, dueDate);
    
    const transaction: Transaction = {
      id: i + 1,
      referenceNumber: `REF-${type.toUpperCase().substring(0, 3)}-${String(i + 1).padStart(5, '0')}`,
      transactionDate,
      customerId: customer.id,
      customerName: customer.name,
      transactionType: type,
      amount,
      currency,
      status,
      description: transactionDescriptions[Math.floor(Math.random() * transactionDescriptions.length)],
      notes: hasNotes ? "Additional notes regarding this transaction. Please review carefully." : undefined,
      createdBy,
      createdAt: transactionDate,
      modifiedBy,
      modifiedAt: hasModified ? randomDate() : undefined,
      dueDate,
      // New optional columns
      accountCode: customer.id.toString(),
      netAmount,
      vatAmount,
      outstandingAmount,
      onDispute: Math.random() > 0.9, // 10% chance of being disputed
      hasPromise: Math.random() > 0.85, // 15% chance of having a promise
      hasCase: Math.random() > 0.9, // 10% chance of being in a collection case
      subLedger: ["Sales", "Services", "Products", "Consulting", "Support"][Math.floor(Math.random() * 5)],
      orderNumber: `SO-${String(Math.floor(Math.random() * 100000)).padStart(6, '0')}`,
    };
    
    addTypeSpecificFields(transaction, type);
    addOptionalFields(transaction, { hasLineItems, hasDocument, type, amount, customer });
    
    transactions.push(transaction);
  }
  
  // Sort by date descending (newest first)
  return transactions.sort((a, b) => 
    new Date(b.transactionDate).getTime() - new Date(a.transactionDate).getTime()
  );
}
